import styled from "@emotion/styled"
import type { MultiSelectChangeEventDetail } from "@porsche-design-system/components-react"
import { PCheckbox, PMultiSelect, PMultiSelectOption, POptgroup } from "@porsche-design-system/components-react"
import { fontWeightSemibold, spacingStaticSm } from "@porsche-design-system/components-react/emotion"
// noinspection ES6PreferShortImport
import type { Ref } from "react"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { Tooltip } from "../../tooltip/Tooltip"

export interface SelectOption {
	hexCode?: string
	label: string
	value: string
}

export interface SelectOptionGroup {
	label: string
	options: SelectOption[]
}

interface MultiSelectProps {
	/** The name of the select input.*/
	name: string

	/** The label text.*/
	label?: string

	/** The selected value. */
	value?: string[]

	/**
	 * Disable the select.
	 * @default false
	 */
	disabled?: boolean

	/**
	 * Tooltip text to show when hovering over the disabled select.
	 * If this is present, it implicitly sets `disabled` to `true`.
	 */
	disabledHint?: string

	/* Show or hide label.
	 * For better accessibility it is recommended to show the label.
	 */
	hideLabel?: boolean

	/**
	 * If a value is required.
	 * @default false
	 */
	required?: boolean

	/**
	 * Called when the user attempts to change the selection.
	 * @param value The proposed value after the change.
	 */
	onChange?: (value: string[]) => void

	/** Shown below the select and puts it into the error state. */
	errorMessage?: string

	/** The displayed options with option label and value. */
	options: (SelectOption | SelectOptionGroup)[]

	/**
	 * If set, an additional first option with this label selects or deselects all options at once.
	 * It is checked when every option is selected, indeterminate when only some are, and the select shows
	 * this label instead of listing every option once all of them are selected.
	 */
	selectAllLabel?: string

	ref?: Ref<HTMLElement>
}

/**
 * A select component to select multiple values via a searchable list.
 */
export const MultiSelect = (props: MultiSelectProps) => {
	const {
		disabled: isExplicitlyDisabled = false,
		disabledHint,
		errorMessage,
		onChange,
		options,
		ref,
		selectAllLabel,
		value,
		...rest
	} = props

	const isDisabled = isExplicitlyDisabled || disabledHint !== undefined

	const optionValues = flattenOptions(options).map((option) => option.value)
	const selectedCount = optionValues.filter((optionValue) => value?.includes(optionValue)).length
	const isEverythingSelected = optionValues.length > 0 && selectedCount === optionValues.length
	const isPartiallySelected = selectedCount > 0 && !isEverythingSelected
	const selectAllValue = isEverythingSelected && selectAllLabel !== undefined ? [SELECT_ALL_VALUE, ...value!] : value

	const onSelectChange = (event: CustomEvent<MultiSelectChangeEventDetail>): void => {
		if (!onChange || isDisabled) {
			return
		}

		const proposedValue = event.detail.value as string[]
		if (selectAllLabel === undefined) {
			onChange(proposedValue)
		} else if (proposedValue.includes(SELECT_ALL_VALUE) !== isEverythingSelected) {
			onChange(isEverythingSelected ? [] : optionValues)
		} else {
			onChange(proposedValue.filter((proposedItem) => proposedItem !== SELECT_ALL_VALUE))
		}
	}

	return (
		// this div is necessary to make sure that the tooltip is an internal part of this MultiSelect and doesn't get
		// appended to an outer element, which can affect layouts such as the FormLayout
		<div>
			<Tooltip content={disabledHint}>
				<PMultiSelect
					{...rest}
					ref={ref}
					disabled={isDisabled}
					message={errorMessage}
					state={errorMessage ? "error" : "none"}
					value={selectAllValue}
					onChange={onSelectChange}
				>
					{selectAllLabel !== undefined && isEverythingSelected && (
						<AllSelectedLabel slot="selected">{selectAllLabel}</AllSelectedLabel>
					)}
					{selectAllLabel !== undefined && (
						<PMultiSelectOption
							aria-checked={isPartiallySelected ? "mixed" : undefined}
							value={SELECT_ALL_VALUE}
						>
							<SelectAllLabel>
								{isPartiallySelected && (
									<IndeterminateCheckbox inert>
										<PCheckbox hideLabel indeterminate label={selectAllLabel} />
									</IndeterminateCheckbox>
								)}
								{selectAllLabel}
							</SelectAllLabel>
						</PMultiSelectOption>
					)}
					{options.map((option) => {
						return "options" in option ? (
							<POptgroup key={option.label} label={option.label}>
								{option.options.map(Option)}
							</POptgroup>
						) : (
							<Option key={option.value} {...option} />
						)
					})}
				</PMultiSelect>
			</Tooltip>
		</div>
	)
}

const SELECT_ALL_VALUE = "__select_all__"

const flattenOptions = (options: (SelectOption | SelectOptionGroup)[]): SelectOption[] =>
	options.flatMap((option) => ("options" in option ? option.options : [option]))

const Option = ({ hexCode, label, value }: SelectOption) => (
	<PMultiSelectOption key={value} value={value}>
		{hexCode ? (
			<ColorOption>
				<ColorTile $color={hexCode} />
				{label}
			</ColorOption>
		) : (
			label
		)}
	</PMultiSelectOption>
)

const AllSelectedLabel = styled.span`
	display: flex;
	align-items: center;
	height: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

// The checkbox of a PMultiSelectOption lives in its shadow DOM and has no indeterminate state, so a PCheckbox
// is laid over it, which sits right before the slotted label.
const CHECKBOX_SIZE = "1.75rem"
const CHECKBOX_GAP = "8px"

const SelectAllLabel = styled.span`
	position: relative;
	display: block;
	font-weight: ${fontWeightSemibold};
`

const IndeterminateCheckbox = styled.span`
	position: absolute;
	inset-block-start: calc((100% - ${CHECKBOX_SIZE}) / 2);
	inset-inline-start: calc(-1 * (${CHECKBOX_SIZE} + ${CHECKBOX_GAP}));
	pointer-events: none;
`

const ColorOption = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacingStaticSm};
`

const ColorTile = styled("div", { shouldForwardProp })<{ $color: string }>`
	width: 1rem;
	height: 1rem;
	border-radius: 50%;
	background: ${({ $color }) => $color};
`
